import { useTurnstile } from "../TurnstileContext";
import { getSSORedirectURL, probeSSODomainWithRetry } from "../sso-redirect";
import { ViewType } from "../types";
import { Button, Input, Message } from "./../base-ui/index";
import { getSsoProbeClient } from "@/utils/supabase/client";
import { VIEWS, I18nVariables, RedirectTo } from "@supabase/auth-ui-shared";
import { SupabaseClient } from "@supabase/supabase-js";
import { ArrowLeft } from "lucide-react";
import { useSearchParams } from "next/navigation";
import React, { useState } from "react";

function ForgottenPassword({
  setAuthView,
  supabaseClient,
  redirectTo,
  i18n,
  showLinks = false,
}: {
  setAuthView: (view: ViewType) => void;
  supabaseClient: SupabaseClient;
  redirectTo?: RedirectTo;
  i18n?: I18nVariables;
  showLinks?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { getToken } = useTurnstile();
  const ssoProbeClient = getSsoProbeClient();
  const searchParams = useSearchParams();

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    // Redirect known SSO domains to their IdP. If detection fails after its
    // retry, fall through to password recovery rather than blocking the user.
    const probe = await probeSSODomainWithRetry({
      email,
      supabaseClient: ssoProbeClient,
      getTurnstileToken: getToken,
    });
    if (probe.kind === "sso") {
      const ssoRedirectUrl = await getSSORedirectURL(
        email,
        supabaseClient,
        await getToken(),
        searchParams.get("deeplink")
      );
      if (ssoRedirectUrl) {
        window.location.href = ssoRedirectUrl;
        return;
      }
      setError("Could not start single sign-on. Please try again.");
      setLoading(false);
      return;
    }
    const url = new URL(redirectTo ?? "");
    url.searchParams.set("type", "recovery");
    url.searchParams.set("email", email);
    const captchaToken = await getToken();
    const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
      redirectTo: url.toString(),
      captchaToken,
    });
    if (error) {
      setError(error.message);
    } else setMessage(i18n?.forgotten_password?.confirmation_text as string);
    setLoading(false);
  };

  const labels = i18n?.forgotten_password;

  return (
    <form
      id="auth-forgot-password"
      onSubmit={(e) => void handlePasswordReset(e)}
    >
      <div className="flex flex-col gap-3 text-left">
        <Input
          id="email"
          name="email"
          type="email"
          autoFocus
          autoComplete="username"
          aria-label={labels?.email_label}
          placeholder={labels?.email_input_placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        <Button type="submit" color="primary" loading={loading}>
          {loading ? labels?.loading_button_label : labels?.button_label}
        </Button>
        {showLinks && (
          <a
            href="auth?view=sign_in"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              setAuthView(VIEWS.SIGN_IN);
            }}
            className="mt-3 inline-flex items-center justify-center gap-1 text-sm text-blue-500 hover:text-blue-600"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </a>
        )}
        {message && <Message>{message}</Message>}
        {error && <Message color="danger">{error}</Message>}
      </div>
    </form>
  );
}

export { ForgottenPassword };
